import Layout from "@/components/feature/Layout";
import ContactForm from "@/pages/about/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "清晰的信息结构",
    desc: "以分类、筛选与详情为核心，帮助你在最短时间内定位想看的内容。",
  },
  {
    icon: "ri-database-2-line",
    title: "丰富的作品资料",
    desc: "剧情简介、演员表、上映信息一站呈现，作品资料完整且一致。",
  },
  {
    icon: "ri-newspaper-line",
    title: "及时的影视资讯",
    desc: "聚合电影新闻、剧集口碑与行业观察，捕捉创作与市场动向。",
  },
  {
    icon: "ri-heart-3-line",
    title: "用心的浏览体验",
    desc: "响应式布局与流畅交互，桌面与手机端都能舒适浏览。",
  },
];

const guides = [
  "通过顶部导航进入「电影」或「电视剧」频道，可按地区、年份、类型筛选并按评分、热度排序。",
  "在搜索框输入片名、类型或演员关键词，即可快速匹配相关影视作品。",
  "点击任意作品卡片进入详情页，查看剧情简介、演员表与相关推荐。",
  "「资讯」页面按分类浏览行业动态与影视新闻，点击资讯可阅读全文。",
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 聚会的目的在线",
    description:
      "了解聚会的目的在线的平台定位与愿景：一个围绕电影与电视剧的信息门户，提供分类浏览、作品详情与影视资讯，并提供留言反馈入口。",
    keywords: "聚会的目的在线,关于我们,影视门户,联系我们,使用说明",
  });

  return (
    <Layout>
      <div className="w-full bg-primary-500 text-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-background-50/80 mb-4">
            <span className="hover:text-white">首页</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">关于我们</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl">关于聚会的目的在线</h1>
          <p className="mt-3 text-sm md:text-base text-background-50/85 max-w-2xl leading-relaxed">
            我们想做的，是一个安静而专业的影视信息角落——不喧哗，但足够好找、足够清楚。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        {/* Intro */}
        <section className="max-w-3xl">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
            我们的定位
          </h2>
          <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
            <p>
              聚会的目的在线是一个围绕电影与电视剧的信息门户。我们把散落的作品信息重新整理，
              让剧情介绍、演员表与上映资料变得更容易被找到和理解。
            </p>
            <p>
              我们相信，好的浏览体验来自清晰的结构：分类、筛选、详情与资讯各安其位，
              你不需要在繁杂的页面里来回寻找，就能轻松获得想要的信息。
            </p>
            <p>
              未来，我们希望能收录更多优秀作品，接入更完整的数据来源，让这里成为你了解影视世界的可靠入口。
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950 mb-6">
            我们在意什么
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading font-semibold text-base text-foreground-950">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Usage + contact */}
        <section className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950 mb-5">
              使用说明
            </h2>
            <ol className="space-y-4">
              {guides.map((g, i) => (
                <li key={g} className="flex gap-4">
                  <span className="w-7 h-7 shrink-0 rounded-md bg-primary-500 text-background-50 text-sm font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{g}</p>
                </li>
              ))}
            </ol>

            <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="font-heading font-semibold text-base text-foreground-950 mb-3">
                联系方式
              </h3>
              <ul className="space-y-2.5 text-sm text-foreground-700">
                <li className="flex items-center gap-2.5">
                  <i className="ri-mail-line text-primary-500"></i>
                  contact@juhaidemudi-demo.com
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="ri-phone-line text-primary-500"></i>
                  400-000-0000（工作日 9:00 - 18:00）
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="ri-map-pin-line text-primary-500"></i>
                  演示地址：某市某区某路 108 号
                </li>
              </ul>
            </div>
          </div>

          <ContactForm />
        </section>

        {/* Disclaimer */}
        <section className="mt-12 md:mt-16">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
            <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500"></i>
              版权与免责声明
            </h2>
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <p>
                本站当前展示的所有影视作品、演员与资讯内容均为演示用途的虚构示例，
                不涉及任何真实作品、真实人物或受版权保护的实际内容。
              </p>
              <p>
                站内搜索与筛选功能均为前端演示实现，不连接真实的后台搜索服务；
                留言表单通过平台内置表单服务收集，仅用于接收用户反馈。
              </p>
              <p>
                如需接入真实数据源或商业使用，请确保内容来源合法合规，并自行取得相应授权。
              </p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}